import { useCallback, useEffect } from "react";
import * as d3 from "d3";

const THEME = {
  ink: "#181010",
  mid: "#6b7280",
  grid: "#e5e7eb",
  bg: "#ffffff",
  brand: "#ff0000",
  palette: [
    "#4E79A7",
    "#F28E2B",
    "#E15759",
    "#76B7B2",
    "#59A14F",
    "#EDC948",
    "#B07AA1",
    "#FF9DA7",
    "#9C755F",
    "#BAB0AC",
  ],
  lineStroke: "#2563EB",
  pointFill: "#1D4ED8",
  areaFill: "#93C5FD",
  areaStroke: "#1D4ED8",
};

const OUTCOME_PARTY_COLORS = {
  liberal: "#D71920",
  conservative: "#002D52",
  ndp: "#F58220",
  bloc: "#33B2CC",
  green: "#3D9B35",
};

const normalizeText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const resolveOutcomeOnlyPartyColor = (selectedDepMeta, hasComparison) => {
  if (hasComparison || !selectedDepMeta) return null;

  const depId = normalizeText(selectedDepMeta?.id);
  if (depId.includes("_party_ratings_23")) return OUTCOME_PARTY_COLORS.liberal;
  if (depId.includes("_party_ratings_24")) return OUTCOME_PARTY_COLORS.conservative;
  if (depId.includes("_party_ratings_25")) return OUTCOME_PARTY_COLORS.ndp;
  if (depId.includes("_party_ratings_26")) return OUTCOME_PARTY_COLORS.bloc;
  if (depId.includes("_party_ratings_27")) return OUTCOME_PARTY_COLORS.green;

  const depText = normalizeText(
    `${selectedDepMeta?.label || ""} ${selectedDepMeta?.question || ""}`
  );
  const isPartyThermometer =
    depText.includes("party thermometer") ||
    depText.includes("feeling toward") ||
    depText.includes("feelings toward");
  if (!isPartyThermometer) return null;

  if (depText.includes("liberal party")) return OUTCOME_PARTY_COLORS.liberal;
  if (depText.includes("conservative party")) return OUTCOME_PARTY_COLORS.conservative;
  if (depText.includes("new democratic party") || depText.includes("ndp")) {
    return OUTCOME_PARTY_COLORS.ndp;
  }
  if (depText.includes("bloc quebecois")) return OUTCOME_PARTY_COLORS.bloc;
  if (depText.includes("green party")) return OUTCOME_PARTY_COLORS.green;

  return null;
};

const wrapText = (selection, width, lineHeightEm = 1.1) => {
  selection.each(function () {
    const text = d3.select(this);
    const words = (text.text() || "").split(/\s+/).filter(Boolean);
    text.text(null);

    let line = [];
    let lineNumber = 0;
    const x = text.attr("x") || 0;
    const y = text.attr("y") || 0;
    const dy = parseFloat(text.attr("dy") || 0);

    let tspan = text.append("tspan").attr("x", x).attr("y", y).attr("dy", `${dy}em`);

    for (let i = 0; i < words.length; i++) {
      line.push(words[i]);
      tspan.text(line.join(" "));
      if (tspan.node() && tspan.node().getComputedTextLength() > width) {
        line.pop();
        tspan.text(line.join(" "));
        line = [words[i]];
        tspan = text
          .append("tspan")
          .attr("x", x)
          .attr("y", y)
          .attr("dy", `${++lineNumber * lineHeightEm + dy}em`)
          .text(words[i]);
      }
    }
  });
};

const getPointerCoordinates = (event) => {
  const sourceEvent = event?.sourceEvent || event;
  return {
    x: sourceEvent?.clientX ?? event?.clientX ?? 0,
    y: sourceEvent?.clientY ?? event?.clientY ?? 0,
  };
};

const getRowWeight = (row) => {
  const parsed = Number(row?.weight);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(0, parsed);
};

const formatCountValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  if (Math.abs(numeric - Math.round(numeric)) < 1e-6) return Math.round(numeric).toLocaleString();
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });
};

const buildCatCatDistribution = (rows, dependentAnswerMapping) => {
  const cleanRows = rows.filter((row) => row?.group && row?.dep && getRowWeight(row) > 0);
  const indepCats = Array.from(new Set(cleanRows.map((row) => String(row.group).trim()))).filter(
    Boolean
  );
  const depMap = dependentAnswerMapping || {};
  const presentDepLabels = Array.from(
    new Set(cleanRows.map((row) => String(row.dep).trim()).filter(Boolean))
  );
  const presentDepSet = new Set(presentDepLabels);

  const depOrderFromMapping = Object.entries(depMap)
    .map(([raw, value]) => {
      const mapped = String(value?.Display ?? value?.display ?? raw).trim();
      return mapped || null;
    })
    .filter(Boolean);

  const orderedMapped = depOrderFromMapping.filter((label) => presentDepSet.has(label));
  const orderedMappedSet = new Set(orderedMapped);
  const remaining = presentDepLabels
    .filter((label) => !orderedMappedSet.has(label))
    .sort((a, b) => a.localeCompare(b));
  const depCats = [...orderedMapped, ...remaining];

  const byIndep = d3.group(cleanRows, (row) => String(row.group).trim());
  const table = [];
  for (const [groupLabel, values] of byIndep) {
    const total = d3.sum(values, getRowWeight) || 1;
    const byDep = d3.rollup(
      values,
      (bucketRows) => d3.sum(bucketRows, getRowWeight),
      (row) => String(row.dep).trim()
    );
    for (const depLabel of depCats) {
      const count = byDep.get(depLabel) || 0;
      table.push({
        indep: groupLabel,
        dep: depLabel,
        count,
        pct: (count / total) * 100,
      });
    }
  }

  return { indepCats, depCats, table };
};

const drawCategoricalLegend = ({ g, innerW, width = 200, title, categories, color }) => {
  const legendG = g
    .append("g")
    .attr("class", "legend")
    .attr("transform", `translate(${innerW - width}, 0)`);

  legendG
    .append("text")
    .attr("x", 0)
    .attr("y", 0)
    .attr("dy", "0.9em")
    .attr("fill", THEME.mid)
    .style("font-weight", 600)
    .style("font-size", "12px")
    .text(title);

  const legendItems = legendG
    .selectAll("g.item")
    .data(categories)
    .join("g")
    .attr("class", "item")
    .attr("transform", (d, i) => `translate(0, ${18 + i * 18})`);

  legendItems
    .append("rect")
    .attr("width", 12)
    .attr("height", 12)
    .attr("rx", 2)
    .attr("fill", (d) => color(d));

  legendItems
    .append("text")
    .attr("x", 16)
    .attr("y", 10)
    .attr("fill", THEME.mid)
    .style("font-size", "12px")
    .text((d) => d);
};

export const useChartRenderer = ({
  graphData,
  chartType,
  selectedIndepMeta,
  selectedDepMeta,
  dependentAnswerMapping,
  containerRef,
  tooltipRef,
}) => {
  const showTooltip = useCallback(
    (html, x, y) => {
      const tip = tooltipRef.current;
      if (!tip) return;
      tip.innerHTML = html;
      tip.style.left = `${x + 12}px`;
      tip.style.top = `${y + 12}px`;
      tip.style.opacity = "1";
      tip.setAttribute("aria-hidden", "false");
    },
    [tooltipRef]
  );

  const hideTooltip = useCallback(() => {
    const tip = tooltipRef.current;
    if (tip) {
      tip.style.opacity = "0";
      tip.setAttribute("aria-hidden", "true");
    }
  }, [tooltipRef]);

  const drawChart = useCallback(() => {
    const container = containerRef.current;
    if (!container || !graphData?.length || !chartType) return;

    const measureTickLabelHeight = (axisG) => {
      let maxH = 0;
      axisG.selectAll("text").each(function () {
        const box = this.getBBox();
        maxH = Math.max(maxH, box.height);
      });
      return maxH;
    };

    const margin = { top: 36, right: 32, bottom: 170, left: 96 };
    const targetHeight = 640;
    const targetWidth = container.clientWidth || 800;

    const svg = d3
      .select(container)
      .selectAll("svg")
      .data([null])
      .join("svg")
      .attr("width", targetWidth)
      .attr("height", targetHeight);

    svg.on("mousemove", null).on("mouseleave", null).on("mouseenter", null);
    svg.on(".density", null);
    hideTooltip();
    svg.selectAll("g.chart-root").remove();

    const innerW = targetWidth - margin.left - margin.right;
    const innerH = targetHeight - margin.top - margin.bottom;

    const g = svg
      .append("g")
      .attr("class", "chart-root")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const drawXAxisTitleAdaptive = (title, axisG, baseOffset = 22) => {
      const labelHeight = measureTickLabelHeight(axisG);
      g.append("text")
        .attr("x", innerW / 2)
        .attr("y", innerH + Math.max(28, labelHeight + baseOffset))
        .attr("text-anchor", "middle")
        .attr("fill", THEME.mid)
        .style("font-weight", 600)
        .style("font-size", "14px")
        .text(title || "");
    };

    const keepYTicksHorizontal = (axisSel) => {
      axisSel.selectAll(".tick text").attr("transform", null).attr("text-anchor", "end");
    };

    const styleAxes = () => {
      g.selectAll(".tick text").attr("fill", THEME.mid);
      g.selectAll(".tick line").attr("stroke", THEME.grid);
      g.selectAll(".domain").attr("stroke", THEME.grid);
    };

    const drawGridYForWidth = (y, width) =>
      g
        .append("g")
        .attr("class", "y-grid")
        .call(d3.axisLeft(y).tickSize(-width).tickFormat(""))
        .call((grid) => grid.selectAll("line").attr("stroke", THEME.grid).attr("stroke-opacity", 1))
        .call((grid) => grid.select(".domain").remove());

    const drawGridY = (y) => drawGridYForWidth(y, innerW);

    const wrapTicks = (xAxisSel, tickWidth) => {
      xAxisSel
        .selectAll("text")
        .style("text-anchor", "middle")
        .attr("dy", "0.9em")
        .call(wrapText, tickWidth);
    };

    const applyWrappedXAxisTicks = (axisSel, labels = [], maxWidth = 160) => {
      const count = Math.max(1, labels.length);
      const tickWidth = Math.max(72, Math.min(maxWidth, innerW / count - 8));
      wrapTicks(axisSel, tickWidth);
    };

    const drawYAxisTitle = (text = "Frequency (%)") => {
      g.append("text")
        .attr("transform", "rotate(-90)")
        .attr("y", -40)
        .attr("x", -innerH / 2)
        .attr("dy", "-0.8em")
        .attr("text-anchor", "middle")
        .attr("fill", THEME.mid)
        .style("font-weight", 600)
        .style("font-size", "14px")
        .text(text);
    };

    const indepMeta = selectedIndepMeta;
    const depMeta = selectedDepMeta;
    const hasComparison = Boolean(selectedIndepMeta);
    const outcomeOnlyPartyColor = resolveOutcomeOnlyPartyColor(selectedDepMeta, hasComparison);
    const indepIsCat = graphData.some((row) => row.group != null);
    const depIsNum = graphData.some((row) => Number.isFinite(row.y));

    const epanechnikov = (k) => (v) => (Math.abs((v /= k)) <= 1 ? (0.75 * (1 - v * v)) / k : 0);
    const kernelDensityEstimator = (kernel, X) => (points) => {
      const totalWeight = d3.sum(points, (point) => point.weight);
      if (!totalWeight) return X.map((x) => [x, 0]);
      return X.map((x) => [
        x,
        d3.sum(points, (point) => kernel(x - point.value) * point.weight) / totalWeight,
      ]);
    };

    if (chartType === "grouped_bar") {
      const { indepCats, depCats, table } = buildCatCatDistribution(
        graphData,
        dependentAnswerMapping
      );

      const x0 = d3.scaleBand().domain(indepCats).range([0, innerW]).padding(0.2);
      const x1 = d3.scaleBand().domain(depCats).range([0, x0.bandwidth()]).padding(0.12);
      const y = d3.scaleLinear().domain([0, 100]).nice().range([innerH, 0]);
      const color = d3.scaleOrdinal().domain(depCats).range(d3.schemeTableau10);

      const xAxis = g
        .append("g")
        .attr("transform", `translate(0,${innerH})`)
        .call(d3.axisBottom(x0));
      applyWrappedXAxisTicks(xAxis, indepCats, 140);
      drawXAxisTitleAdaptive(indepMeta?.question ?? "", xAxis);

      const yAxis = g.append("g").call(d3.axisLeft(y));
      keepYTicksHorizontal(yAxis);
      drawYAxisTitle("Frequency (%)");
      drawGridY(y).style("pointer-events", "none");

      const indepG = g
        .selectAll(".indep-g")
        .data(indepCats)
        .join("g")
        .attr("class", "indep-g")
        .attr("transform", (d) => `translate(${x0(d)},0)`);

      const bars = indepG
        .selectAll("rect.bar")
        .data((indep) =>
          depCats.map((dep) => {
            const record = table.find((row) => row.indep === indep && row.dep === dep);
            return { indep, dep, val: record ? record.pct : 0, count: record ? record.count : 0 };
          })
        )
        .join("rect")
        .attr("class", "bar")
        .attr("x", (d) => x1(d.dep))
        .attr("y", (d) => y(d.val))
        .attr("width", x1.bandwidth())
        .attr("height", (d) => innerH - y(d.val))
        .attr("fill", (d) => color(d.dep))
        .style("pointer-events", "all");

      bars
        .on("mouseenter", (event, d) => {
          const { x, y: yy } = getPointerCoordinates(event);
          showTooltip(
            `<div style="font-weight:600">${d.indep}</div>
            <div>${d.dep}: ${d.val.toFixed(1)}% (n=${formatCountValue(d.count)})</div>`,
            x,
            yy
          );
        })
        .on("mousemove", (event, d) => {
          const { x, y: yy } = getPointerCoordinates(event);
          showTooltip(
            `<div style="font-weight:600">${d.indep}</div>
            <div>${d.dep}: ${d.val.toFixed(1)}% (n=${formatCountValue(d.count)})</div>`,
            x,
            yy
          );
        })
        .on("mouseleave", hideTooltip);
      drawCategoricalLegend({
        g,
        innerW,
        width: 200,
        title: depMeta?.question ?? "Outcome",
        categories: depCats,
        color,
      });

      styleAxes();
    } else if (chartType === "heatmap") {
      const { indepCats, depCats, table } = buildCatCatDistribution(
        graphData,
        dependentAnswerMapping
      );
      const cells = table.map((row) => ({
        group: row.indep,
        dep: row.dep,
        value: row.pct,
        count: row.count,
      }));

      const x = d3.scaleBand().domain(indepCats).range([0, innerW]).padding(0.05);
      const y = d3.scaleBand().domain(depCats).range([0, innerH]).padding(0.05);
      const maxV = d3.max(cells, (d) => d.value) || 1;
      const color = d3.scaleSequential(d3.interpolateBlues).domain([0, maxV]);

      const xAxis = g
        .append("g")
        .attr("transform", `translate(0,${innerH})`)
        .call(d3.axisBottom(x));
      applyWrappedXAxisTicks(xAxis, indepCats, 140);

      const yAxis = g.append("g").call(d3.axisLeft(y));
      keepYTicksHorizontal(yAxis);
      drawXAxisTitleAdaptive(indepMeta?.question ?? "", xAxis);
      drawYAxisTitle(depMeta?.question ?? "");

      g.selectAll("rect.cell")
        .data(cells)
        .join("rect")
        .attr("class", "cell")
        .attr("x", (d) => x(d.group))
        .attr("y", (d) => y(d.dep))
        .attr("width", x.bandwidth())
        .attr("height", y.bandwidth())
        .attr("fill", (d) => color(d.value))
        .on("mousemove", (event, d) => {
          const { x, y: yy } = getPointerCoordinates(event);
          showTooltip(
            `<div><strong>${d.dep}</strong> — ${d.group}</div>
            <div>Total Count: ${formatCountValue(d.count)}</div>
            <div>Frequency: ${d.value.toFixed(1)}%</div>`,
            x,
            yy
          );
        })
        .on("mouseleave", hideTooltip);

      styleAxes();
    } else if (chartType === "lollipop") {
      const { indepCats, depCats, table } = buildCatCatDistribution(
        graphData,
        dependentAnswerMapping
      );

      const x = d3.scaleBand().domain(indepCats).range([0, innerW]).padding(0.25);
      const x1 = d3.scaleBand().domain(depCats).range([0, x.bandwidth()]).padding(0.4);
      const y = d3.scaleLinear().domain([0, 100]).nice().range([innerH, 0]);
      const color = d3.scaleOrdinal().domain(depCats).range(d3.schemeTableau10);

      const xAxis = g
        .append("g")
        .attr("transform", `translate(0,${innerH})`)
        .call(d3.axisBottom(x));
      applyWrappedXAxisTicks(xAxis, indepCats, 130);

      drawXAxisTitleAdaptive(indepMeta?.question ?? "", xAxis);

      const yAxis = g
        .append("g")
        .attr("class", "y-axis")
        .call(
          d3
            .axisLeft(y)
            .ticks(6)
            .tickFormat((tick) => `${tick}%`)
        );
      yAxis.selectAll(".tick text").attr("transform", null);
      drawYAxisTitle("Frequency (%)");
      drawGridY(y).style("pointer-events", "none");

      const groupsG = g
        .selectAll(".indep-g")
        .data(indepCats)
        .join("g")
        .attr("class", "indep-g")
        .attr("transform", (d) => `translate(${x(d)},0)`);

      groupsG
        .selectAll("line.stem")
        .data((indep) =>
          depCats.map((dep) => {
            const record = table.find((row) => row.indep === indep && row.dep === dep);
            return {
              indep,
              dep,
              pct: record ? record.pct : 0,
              count: record ? record.count : 0,
            };
          })
        )
        .join("line")
        .attr("class", "stem")
        .attr("x1", (d) => x1(d.dep) + x1.bandwidth() / 2)
        .attr("x2", (d) => x1(d.dep) + x1.bandwidth() / 2)
        .attr("y1", y(0))
        .attr("y2", (d) => y(d.pct))
        .attr("stroke", THEME.grid)
        .attr("stroke-width", 2);

      const heads = groupsG
        .selectAll("circle.head")
        .data((indep) =>
          depCats.map((dep) => {
            const record = table.find((row) => row.indep === indep && row.dep === dep);
            return {
              indep,
              dep,
              pct: record ? record.pct : 0,
              count: record ? record.count : 0,
            };
          })
        )
        .join("circle")
        .attr("class", "head")
        .attr("cx", (d) => x1(d.dep) + x1.bandwidth() / 2)
        .attr("cy", (d) => y(d.pct))
        .attr("r", 5)
        .attr("fill", (d) => color(d.dep))
        .style("pointer-events", "all");

      heads
        .on("mouseenter", (event, d) => {
          const { x, y: yy } = getPointerCoordinates(event);
          showTooltip(
            `<div style="font-weight:600">${d.dep}</div>
            <div>${d.indep}: ${d.pct.toFixed(1)}% (n=${formatCountValue(d.count)})</div>`,
            x,
            yy
          );
        })
        .on("mousemove", (event, d) => {
          const { x, y: yy } = getPointerCoordinates(event);
          showTooltip(
            `<div style="font-weight:600">${d.dep}</div>
            <div>${d.indep}: ${d.pct.toFixed(1)}% (n=${formatCountValue(d.count)})</div>`,
            x,
            yy
          );
        })
        .on("mouseleave", hideTooltip);
      drawCategoricalLegend({
        g,
        innerW,
        width: 220,
        title: depMeta?.question ?? "Outcome",
        categories: depCats,
        color,
      });

      styleAxes();
    } else if (chartType === "density") {
      const depType = String(selectedDepMeta?.type || "").toLowerCase();
      const indepType = String(selectedIndepMeta?.type || "").toLowerCase();
      const hasGroupValues = graphData.some((row) => row.group != null && String(row.group) !== "");
      const hasDepCategories = graphData.some((row) => row.dep != null && String(row.dep) !== "");

      let valueAccessor = null;
      let categoryAccessor = null;
      let xAxisTitle = selectedDepMeta?.question ?? selectedDepMeta?.label ?? "Outcome Value";
      let legendTitle = "Group";

      if (depType === "num") {
        valueAccessor = (row) => Number(row.y);
        xAxisTitle = selectedDepMeta?.question ?? selectedDepMeta?.label ?? "Outcome Value";
        if (hasGroupValues) {
          categoryAccessor = (row) => String(row.group);
          legendTitle =
            selectedIndepMeta?.question ?? selectedIndepMeta?.label ?? "Comparison Category";
        }
      } else if (indepType === "num") {
        valueAccessor = (row) => Number(row.x);
        xAxisTitle = selectedIndepMeta?.question ?? selectedIndepMeta?.label ?? "Comparison Value";
        if (hasDepCategories) {
          categoryAccessor = (row) => String(row.dep);
          legendTitle = selectedDepMeta?.question ?? selectedDepMeta?.label ?? "Outcome";
        }
      } else {
        valueAccessor = (row) => (Number.isFinite(Number(row.y)) ? Number(row.y) : Number(row.x));
      }

      const validRows = graphData.filter(
        (row) => Number.isFinite(valueAccessor(row)) && getRowWeight(row) > 0
      );
      if (!validRows.length) return;

      const allValues = validRows.map((row) => valueAccessor(row));
      let [minValue, maxValue] = d3.extent(allValues);
      if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) return;
      if (minValue === maxValue) {
        const pad = Math.max(Math.abs(minValue) * 0.01, 0.5);
        minValue -= pad;
        maxValue += pad;
      }
      const observedMin = d3.min(allValues);
      const observedMax = d3.max(allValues);
      const integerLikeShare = allValues.length
        ? d3.mean(allValues, (value) => (Math.abs(value - Math.round(value)) < 1e-6 ? 1 : 0))
        : 0;
      const uniqueRoundedCount = new Set(allValues.map((value) => Math.round(value))).size;
      const looksBoundedSurveyScale =
        (observedMin >= 0 && observedMax <= 10.5) ||
        (observedMin >= 0 && observedMax <= 100.5) ||
        (observedMin >= 0 && observedMax <= 120 && uniqueRoundedCount <= 121);
      const useBoundedDiscreteDensity =
        integerLikeShare >= 0.95 && looksBoundedSurveyScale && uniqueRoundedCount > 1;

      const groupedSeriesRaw = categoryAccessor
        ? Array.from(d3.group(validRows, categoryAccessor).entries())
        : [["All respondents", validRows]];
      const hasMultiSeries = groupedSeriesRaw.length > 1;
      const legendSpace = hasMultiSeries ? 190 : 0;
      const plotWidth = Math.max(220, innerW - legendSpace);

      const observedSpan = Math.max(1e-6, maxValue - minValue);
      const x = d3.scaleLinear().domain([minValue, maxValue]).range([0, plotWidth]);
      const discreteStart = Math.ceil(minValue);
      const discreteEnd = Math.floor(maxValue);
      const discreteSupport = useBoundedDiscreteDensity
        ? d3.range(discreteStart, discreteEnd + 1, 1)
        : [];
      const densityTickCount = Math.max(64, Math.min(256, Math.round(plotWidth / 4)));
      const densityTicksRaw = d3.ticks(minValue, maxValue, densityTickCount);
      const densityTicks = densityTicksRaw.length ? densityTicksRaw.slice() : [minValue, maxValue];
      if (densityTicks[0] > minValue) densityTicks.unshift(minValue);
      if (densityTicks[densityTicks.length - 1] < maxValue) densityTicks.push(maxValue);

      const computeBandwidth = (values) => {
        const sampleSize = values.length;
        if (sampleSize <= 1) return Math.max(observedSpan / 12, 1e-6);

        const sorted = values.slice().sort((a, b) => a - b);
        const stdDev = d3.deviation(sorted) || 0;
        const q1 = d3.quantileSorted(sorted, 0.25);
        const q3 = d3.quantileSorted(sorted, 0.75);
        const iqrSigma =
          Number.isFinite(q1) && Number.isFinite(q3) && q3 > q1 ? (q3 - q1) / 1.34 : 0;
        const robustSigmaCandidates = [stdDev, iqrSigma].filter((value) => value > 0);
        const robustSigma = robustSigmaCandidates.length
          ? Math.min(...robustSigmaCandidates)
          : Math.max(stdDev, iqrSigma, observedSpan / 20);

        const silverman = 0.9 * robustSigma * Math.pow(sampleSize, -1 / 5);
        const scott = stdDev > 0 ? 1.06 * stdDev * Math.pow(sampleSize, -1 / 5) : NaN;
        const candidate = [silverman, scott]
          .filter((value) => Number.isFinite(value) && value > 0)
          .sort((a, b) => a - b)[0];

        const fallback = Math.max(observedSpan / 20, 1e-6);
        const unclamped = candidate || fallback;
        const minBandwidth = Math.max(observedSpan / 300, 1e-6);
        const maxBandwidth = Math.max(observedSpan / 2, minBandwidth);
        return Math.min(maxBandwidth, Math.max(minBandwidth, unclamped));
      };

      const series = groupedSeriesRaw
        .map(([category, rows]) => {
          const points = rows
            .map((row) => ({
              value: valueAccessor(row),
              weight: getRowWeight(row),
            }))
            .filter((point) => Number.isFinite(point.value) && point.weight > 0);
          const values = points.map((point) => point.value);
          const totalWeight = d3.sum(points, (point) => point.weight);
          if (!values.length) return null;

          if (useBoundedDiscreteDensity && discreteSupport.length > 1) {
            const roundedPoints = points
              .map((point) => ({
                value: Math.round(point.value),
                weight: point.weight,
              }))
              .filter((point) => point.value >= discreteStart && point.value <= discreteEnd);
            const countByValue = d3.rollup(
              roundedPoints,
              (bucketRows) => d3.sum(bucketRows, (point) => point.weight),
              (point) => point.value
            );
            const basePoints = discreteSupport.map((xValue) => [
              xValue,
              (countByValue.get(xValue) || 0) / Math.max(totalWeight, 1e-9),
            ]);
            const smoothedPoints = basePoints.map((point, index, allPoints) => {
              const prev = allPoints[index - 1]?.[1] ?? point[1];
              const next = allPoints[index + 1]?.[1] ?? point[1];
              return [point[0], (prev + point[1] * 2 + next) / 4];
            });

            return {
              category,
              sampleSize: totalWeight,
              density: smoothedPoints,
            };
          }

          const bandwidth = computeBandwidth(values);
          const kde = kernelDensityEstimator(epanechnikov(bandwidth), densityTicks);

          return {
            category,
            sampleSize: totalWeight,
            density: kde(points),
          };
        })
        .filter(Boolean)
        .filter((item) => item.sampleSize > 0 && item.density.length > 0);
      if (!series.length) return;

      const yMax = d3.max(series.flatMap((item) => item.density.map((point) => point[1]))) || 0.001;
      const y = d3.scaleLinear().domain([0, yMax]).nice().range([innerH, 0]);

      const xAxis = g
        .append("g")
        .attr("transform", `translate(0,${innerH})`)
        .call(d3.axisBottom(x));
      const yAxis = g.append("g").call(d3.axisLeft(y));
      keepYTicksHorizontal(yAxis);
      drawGridYForWidth(y, plotWidth).style("pointer-events", "none");

      const labelHeight = measureTickLabelHeight(xAxis);
      g.append("text")
        .attr("x", plotWidth / 2)
        .attr("y", innerH + Math.max(28, labelHeight + 22))
        .attr("text-anchor", "middle")
        .attr("fill", THEME.mid)
        .style("font-weight", 600)
        .style("font-size", "14px")
        .text(xAxisTitle);
      drawYAxisTitle("Density");

      const clipId = `density-clip-${Math.random().toString(36).slice(2)}`;
      g.append("defs")
        .append("clipPath")
        .attr("id", clipId)
        .append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", plotWidth)
        .attr("height", innerH);
      const densityLayer = g.append("g").attr("clip-path", `url(#${clipId})`);

      const line = d3
        .line()
        .x((d) => x(d[0]))
        .y((d) => y(d[1]))
        .curve(useBoundedDiscreteDensity ? d3.curveMonotoneX : d3.curveBasis);
      const area = d3
        .area()
        .x((d) => x(d[0]))
        .y0(y(0))
        .y1((d) => y(d[1]))
        .curve(useBoundedDiscreteDensity ? d3.curveMonotoneX : d3.curveBasis);
      const color = d3
        .scaleOrdinal()
        .domain(series.map((item) => item.category))
        .range(d3.schemeTableau10);

      if (hasMultiSeries) {
        series.forEach((item) => {
          const colorValue = color(item.category);
          densityLayer
            .append("path")
            .datum(item.density)
            .attr("fill", colorValue)
            .attr("opacity", 0.08)
            .attr("d", area);

          densityLayer
            .append("path")
            .datum(item.density)
            .attr("fill", "none")
            .attr("stroke", colorValue)
            .attr("stroke-width", 2.2)
            .attr("d", line);
        });

        const legendG = g.append("g").attr("transform", `translate(${plotWidth + 18}, 0)`);
        legendG
          .append("text")
          .attr("x", 0)
          .attr("y", 0)
          .attr("dy", "0.9em")
          .attr("fill", THEME.mid)
          .style("font-weight", 600)
          .style("font-size", "12px")
          .text(legendTitle);

        const legendItems = legendG
          .selectAll("g.item")
          .data(series.map((item) => item.category))
          .join("g")
          .attr("class", "item")
          .attr("transform", (category, index) => `translate(0, ${18 + index * 18})`);

        legendItems
          .append("rect")
          .attr("width", 12)
          .attr("height", 12)
          .attr("rx", 2)
          .attr("fill", (category) => color(category));

        legendItems
          .append("text")
          .attr("x", 16)
          .attr("y", 10)
          .attr("fill", THEME.mid)
          .style("font-size", "12px")
          .text((category) => category);
      } else {
        const onlySeries = series[0];
        const singleSeriesFill = outcomeOnlyPartyColor || THEME.areaFill;
        const singleSeriesStroke = outcomeOnlyPartyColor || THEME.areaStroke;
        densityLayer
          .append("path")
          .datum(onlySeries.density)
          .attr("fill", singleSeriesFill)
          .attr("opacity", outcomeOnlyPartyColor ? 0.24 : 0.8)
          .attr("d", area);
        densityLayer
          .append("path")
          .datum(onlySeries.density)
          .attr("fill", "none")
          .attr("stroke", singleSeriesStroke)
          .attr("stroke-width", 2)
          .attr("d", line);
      }

      styleAxes();
    } else if (chartType === "scatter_marginal") {
      const pts = graphData.filter((d) => Number.isFinite(d.x) && Number.isFinite(d.y));
      if (!pts.length) return;

      const x = d3
        .scaleLinear()
        .domain(d3.extent(pts, (d) => d.x))
        .nice()
        .range([0, innerW]);
      const y = d3
        .scaleLinear()
        .domain(d3.extent(pts, (d) => d.y))
        .nice()
        .range([innerH, 0]);

      const xAxis = g
        .append("g")
        .attr("transform", `translate(0,${innerH})`)
        .call(d3.axisBottom(x));
      const yAxis = g.append("g").call(d3.axisLeft(y));
      keepYTicksHorizontal(yAxis);
      const xLabel = selectedIndepMeta?.question ?? selectedIndepMeta?.label ?? "Comparison Value";
      const yLabel = selectedDepMeta?.question ?? selectedDepMeta?.label ?? "Outcome Value";
      drawXAxisTitleAdaptive(xLabel, xAxis);
      drawYAxisTitle(yLabel);
      drawGridY(y);

      const points = g
        .selectAll("circle.point")
        .data(pts)
        .join("circle")
        .attr("class", "point")
        .attr("cx", (d) => x(d.x))
        .attr("cy", (d) => y(d.y))
        .attr("r", 3.5)
        .attr("fill", "#1d4ed8")
        .attr("opacity", 0.85)
        .style("pointer-events", "all");

      points
        .on("mouseenter", (event, d) => {
          const { x: xx, y: yy } = getPointerCoordinates(event);
          const pointWeight = getRowWeight(d);
          showTooltip(
            `<div><strong>${xLabel} vs ${yLabel}</strong></div>
            <div>${xLabel}: ${Number(d.x).toFixed(2)}</div>
            <div>${yLabel}: ${Number(d.y).toFixed(2)}</div>
            <div>Total Count: ${formatCountValue(pointWeight)}</div>`,
            xx,
            yy
          );
        })
        .on("mousemove", (event, d) => {
          const { x: xx, y: yy } = getPointerCoordinates(event);
          const pointWeight = getRowWeight(d);
          showTooltip(
            `<div><strong>${xLabel} vs ${yLabel}</strong></div>
            <div>${xLabel}: ${Number(d.x).toFixed(2)}</div>
            <div>${yLabel}: ${Number(d.y).toFixed(2)}</div>
            <div>Total Count: ${formatCountValue(pointWeight)}</div>`,
            xx,
            yy
          );
        })
        .on("mouseleave", hideTooltip);

      g.append("g")
        .selectAll("line.xrug")
        .data(pts.map((p) => p.x))
        .join("line")
        .attr("x1", (d) => x(d))
        .attr("x2", (d) => x(d))
        .attr("y1", innerH)
        .attr("y2", innerH + 6)
        .attr("stroke", THEME.grid);
      g.append("g")
        .selectAll("line.yrug")
        .data(pts.map((p) => p.y))
        .join("line")
        .attr("x1", -6)
        .attr("x2", 0)
        .attr("y1", (d) => y(d))
        .attr("y2", (d) => y(d))
        .attr("stroke", THEME.grid);
    } else if (chartType === "density_2d") {
      const pts = graphData.filter((d) => Number.isFinite(d.x) && Number.isFinite(d.y));
      if (!pts.length) return;
      const totalWeight = d3.sum(pts, getRowWeight);

      const x = d3
        .scaleLinear()
        .domain(d3.extent(pts, (d) => d.x))
        .nice()
        .range([0, innerW]);
      const y = d3
        .scaleLinear()
        .domain(d3.extent(pts, (d) => d.y))
        .nice()
        .range([innerH, 0]);

      const xAxis = g
        .append("g")
        .attr("transform", `translate(0,${innerH})`)
        .call(d3.axisBottom(x));
      const yAxis = g.append("g").call(d3.axisLeft(y));
      keepYTicksHorizontal(yAxis);
      const xLabel = selectedIndepMeta?.question ?? selectedIndepMeta?.label ?? "Comparison Value";
      const yLabel = selectedDepMeta?.question ?? selectedDepMeta?.label ?? "Outcome Value";
      drawXAxisTitleAdaptive(xLabel, xAxis);
      drawYAxisTitle(yLabel);

      const contours = d3
        .contourDensity()
        .x((d) => x(d.x))
        .y((d) => y(d.y))
        .weight((d) => getRowWeight(d))
        .size([innerW, innerH])
        .bandwidth(30)(pts);

      g.append("g")
        .selectAll("path")
        .data(contours)
        .join("path")
        .attr("d", d3.geoPath())
        .attr("fill", "none")
        .attr("stroke", "#1d4ed8")
        .attr("stroke-width", 1.4)
        .attr("opacity", 0.85)
        .style("pointer-events", "all")
        .on("mousemove", (event, contour) => {
          const { x: xx, y: yy } = getPointerCoordinates(event);
          showTooltip(
            `<div><strong>${xLabel} vs ${yLabel}</strong></div>
            <div>Density level: ${Number(contour?.value || 0).toFixed(4)}</div>
            <div>Total Count: ${formatCountValue(totalWeight)}</div>`,
            xx,
            yy
          );
        })
        .on("mouseleave", hideTooltip);
    } else if (
      chartType === "ridgeline" ||
      chartType === "boxplot" ||
      chartType === "hist_stacked"
    ) {
      const isIndepCat = indepIsCat && depIsNum;
      const catKey = isIndepCat ? "group" : "dep";
      const numKey = isIndepCat ? "y" : "x";
      const valueAxisLabel = isIndepCat
        ? (selectedDepMeta?.question ?? selectedDepMeta?.label ?? "Outcome Value")
        : (selectedIndepMeta?.question ?? selectedIndepMeta?.label ?? "Comparison Value");
      const categoryAxisLabel = isIndepCat
        ? (selectedIndepMeta?.question ?? selectedIndepMeta?.label ?? "Comparison Category")
        : (selectedDepMeta?.question ?? selectedDepMeta?.label ?? "Outcome Category");

      const byCat = d3.group(
        graphData.filter((d) => Number.isFinite(d[numKey])),
        (d) => d[catKey]
      );
      const series = Array.from(byCat, ([category, rows]) => {
        const points = rows
          .map((row) => ({
            value: Number(row[numKey]),
            weight: getRowWeight(row),
          }))
          .filter((point) => Number.isFinite(point.value) && point.weight > 0);
        return {
          category,
          points,
          values: points.map((point) => point.value),
          totalWeight: d3.sum(points, (point) => point.weight),
        };
      })
        .filter((entry) => entry.values.length > 0)
        .sort((a, b) => a.category?.localeCompare?.(b.category) ?? 0);

      if (chartType === "ridgeline") {
        const allVals = series.flatMap((s) => s.values);
        if (!allVals.length) return;

        const x = d3.scaleLinear().domain(d3.extent(allVals)).nice().range([0, innerW]);
        const y = d3
          .scaleBand()
          .domain(series.map((s) => s.category))
          .range([0, innerH])
          .padding(0.2);

        const xAxis = g
          .append("g")
          .attr("transform", `translate(0,${innerH})`)
          .call(d3.axisBottom(x));
        const yAxis = g.append("g").call(d3.axisLeft(y));
        keepYTicksHorizontal(yAxis);
        drawXAxisTitleAdaptive(valueAxisLabel, xAxis);
        drawYAxisTitle(categoryAxisLabel);

        series.forEach((s) => {
          const ticks = d3.range(
            x.domain()[0],
            x.domain()[1],
            (x.domain()[1] - x.domain()[0]) / 128
          );
          const kde = kernelDensityEstimator(
            epanechnikov((x.domain()[1] - x.domain()[0]) / 25),
            ticks
          );
          const density = kde(s.points);
          const ymax = d3.max(density, (d) => d[1]) || 1;
          const yLocal = d3.scaleLinear().domain([0, ymax]).range([y.bandwidth(), 0]);

          const area = d3
            .area()
            .x((d) => x(d[0]))
            .y0(y(s.category) + y.bandwidth())
            .y1((d) => y(s.category) + yLocal(d[1]))
            .curve(d3.curveBasis);

          g.append("path")
            .datum(density)
            .attr("fill", "#93c5fd")
            .attr("opacity", 0.85)
            .attr("d", area)
            .style("pointer-events", "all")
            .on("mousemove", (event) => {
              const { x: xx, y: yy } = getPointerCoordinates(event);
              showTooltip(
                `<div><strong>${s.category}</strong></div>
                <div>Total Count: ${formatCountValue(s.totalWeight)}</div>
                <div>${valueAxisLabel} median: ${Number(d3.median(s.values) || 0).toFixed(2)}</div>`,
                xx,
                yy
              );
            })
            .on("mouseleave", hideTooltip);
        });
      } else if (chartType === "boxplot") {
        const allVals = series.flatMap((s) => s.values);
        if (!allVals.length) return;

        const x = d3
          .scaleBand()
          .domain(series.map((s) => s.category))
          .range([0, innerW])
          .padding(0.3);
        const y = d3.scaleLinear().domain(d3.extent(allVals)).nice().range([innerH, 0]);

        const xAxis = g
          .append("g")
          .attr("transform", `translate(0,${innerH})`)
          .call(d3.axisBottom(x));
        applyWrappedXAxisTicks(
          xAxis,
          series.map((s) => s.category),
          Math.max(88, x.bandwidth() + 12)
        );
        drawXAxisTitleAdaptive(isIndepCat ? indepMeta?.question : depMeta?.question, xAxis);
        const yAxis = g.append("g").call(d3.axisLeft(y));
        keepYTicksHorizontal(yAxis);
        drawYAxisTitle(valueAxisLabel);

        series.forEach((s) => {
          const sorted = s.values.slice().sort((a, b) => a - b);
          const q1 = d3.quantileSorted(sorted, 0.25);
          const q2 = d3.quantileSorted(sorted, 0.5);
          const q3 = d3.quantileSorted(sorted, 0.75);
          const iqr = q3 - q1;
          const lo = Math.max(sorted[0], q1 - 1.5 * iqr);
          const hi = Math.min(sorted[sorted.length - 1], q3 + 1.5 * iqr);

          const cx = x(s.category);
          const bw = x.bandwidth();

          g.append("rect")
            .attr("x", cx + bw * 0.15)
            .attr("y", y(q3))
            .attr("width", bw * 0.7)
            .attr("height", Math.max(1, y(q1) - y(q3)))
            .attr("fill", "#c7d2fe")
            .attr("stroke", "#4338ca")
            .style("pointer-events", "all")
            .on("mousemove", (event) => {
              const { x: xx, y: yy } = getPointerCoordinates(event);
              showTooltip(
                `<div><strong>${s.category}</strong></div>
                <div>Total Count: ${formatCountValue(s.totalWeight)}</div>
                <div>Q1: ${q1.toFixed(2)} | Median: ${q2.toFixed(2)} | Q3: ${q3.toFixed(2)}</div>`,
                xx,
                yy
              );
            })
            .on("mouseleave", hideTooltip);

          g.append("line")
            .attr("x1", cx + bw * 0.15)
            .attr("x2", cx + bw * 0.85)
            .attr("y1", y(q2))
            .attr("y2", y(q2))
            .attr("stroke", "#1d4ed8")
            .attr("stroke-width", 2);

          g.append("line")
            .attr("x1", cx + bw / 2)
            .attr("x2", cx + bw / 2)
            .attr("y1", y(lo))
            .attr("y2", y(q1))
            .attr("stroke", THEME.mid);
          g.append("line")
            .attr("x1", cx + bw / 2)
            .attr("x2", cx + bw / 2)
            .attr("y1", y(q3))
            .attr("y2", y(hi))
            .attr("stroke", THEME.mid);
        });
      } else if (chartType === "hist_stacked") {
        const longRows = series.flatMap((s) =>
          s.points.map((point) => ({
            group: s.category,
            value: point.value,
            weight: point.weight,
          }))
        );
        if (!longRows.length) return;
        const totalDisplayedCount = d3.sum(longRows, (row) => row.weight) || 1;

        const groups = Array.from(new Set(longRows.map((d) => d.group)));
        const x = d3
          .scaleLinear()
          .domain(d3.extent(longRows, (d) => d.value))
          .nice()
          .range([0, innerW]);
        const binsAll = d3.bin().domain(x.domain()).thresholds(20);
        const bins = binsAll(longRows.map((d) => d.value));
        const finalBinUpper = bins.length ? bins[bins.length - 1].x1 : null;

        const stacked = bins.map((bin, binIndex) => {
          const counts = {};
          groups.forEach((group) => {
            counts[group] = 0;
          });
          const isLastBin = binIndex === bins.length - 1;
          longRows.forEach((row) => {
            const inBin =
              row.value >= bin.x0 &&
              (row.value < bin.x1 || (isLastBin && row.value <= finalBinUpper));
            if (inBin) counts[row.group] += row.weight;
          });
          const totalCount = groups.reduce((sum, group) => sum + (counts[group] || 0), 0);
          return { x0: bin.x0, x1: bin.x1, totalCount, ...counts };
        });

        const seriesSt = d3.stack().keys(groups)(stacked);
        const y = d3
          .scaleLinear()
          .domain([0, d3.max(seriesSt[seriesSt.length - 1], (d) => d[1]) || 1])
          .nice()
          .range([innerH, 0]);
        const color = outcomeOnlyPartyColor
          ? d3
              .scaleOrdinal()
              .domain(groups)
              .range(groups.map(() => outcomeOnlyPartyColor))
          : d3.scaleOrdinal().domain(groups).range(d3.schemeTableau10);

        const xAxis = g
          .append("g")
          .attr("transform", `translate(0,${innerH})`)
          .call(d3.axisBottom(x));
        const yAxis = g.append("g").call(d3.axisLeft(y));
        keepYTicksHorizontal(yAxis);
        drawXAxisTitleAdaptive(valueAxisLabel, xAxis);
        drawYAxisTitle("Total Count");

        const stackGroups = g
          .append("g")
          .selectAll("g.stack")
          .data(seriesSt)
          .join("g")
          .attr("fill", (d) => color(d.key));

        const bars = stackGroups
          .selectAll("rect")
          .data((seriesEntry) =>
            seriesEntry.map((bin) => ({
              ...bin,
              key: seriesEntry.key,
              count: bin.data[seriesEntry.key] || 0,
              frequencyPct: ((bin.data[seriesEntry.key] || 0) / totalDisplayedCount) * 100,
            }))
          )
          .join("rect")
          .attr("x", (d) => x(d.data.x0))
          .attr("y", (d) => y(d[1]))
          .attr("height", (d) => y(d[0]) - y(d[1]))
          .attr("width", (d) => Math.max(1, x(d.data.x1) - x(d.data.x0) - 1))
          .style("pointer-events", "all");

        bars
          .on("mouseenter", (event, d) => {
            const { x: xx, y: yy } = getPointerCoordinates(event);
            showTooltip(
              `<div><strong>${d.key}</strong></div>
              <div>Range: ${d.data.x0.toFixed(2)} - ${d.data.x1.toFixed(2)}</div>
              <div>Total Count: ${formatCountValue(d.count)}</div>
              <div>Frequency (%): ${d.frequencyPct.toFixed(1)}</div>`,
              xx,
              yy
            );
          })
          .on("mousemove", (event, d) => {
            const { x: xx, y: yy } = getPointerCoordinates(event);
            showTooltip(
              `<div><strong>${d.key}</strong></div>
              <div>Range: ${d.data.x0.toFixed(2)} - ${d.data.x1.toFixed(2)}</div>
              <div>Total Count: ${formatCountValue(d.count)}</div>
              <div>Frequency (%): ${d.frequencyPct.toFixed(1)}</div>`,
              xx,
              yy
            );
          })
          .on("mouseleave", hideTooltip);
      }
    }
  }, [
    chartType,
    containerRef,
    dependentAnswerMapping,
    graphData,
    hideTooltip,
    selectedDepMeta,
    selectedIndepMeta,
    showTooltip,
  ]);

  useEffect(() => {
    window.addEventListener("resize", drawChart);
    return () => {
      window.removeEventListener("resize", drawChart);
    };
  }, [drawChart]);

  useEffect(() => {
    drawChart();
  }, [drawChart]);

  useEffect(() => {
    return () => {
      hideTooltip();
    };
  }, [hideTooltip]);
};
